import AppPagination from "@/component/common/AppPagination.tsx";
import { DateHelper } from "@/constant/helper.ts";
import { LicenseService } from "@/services/license/LicenseService.ts";
import { LicenseTableHeader } from "@/view/license/component/LicenseTableHeader.tsx";
import { Form, Table, TableProps } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type SearchFormType = {
  issuedDate?: Date[] | null;
  expiredDate?: Date[] | null;
};

const LicenseManagement = ({
  detailRegistration,
}: {
  detailRegistration?: LicenseRegistrationDetail;
}) => {
  const { t } = useTranslation();

  const [dataTable, setDataTable] = useState<LicenseItemSearch[]>([]);
  const [totalRecord, setTotalRecord] = useState<number>(0);
  const [pagination, setPagination] = useState<PaginationAndSort>({
    size: 10,
    page: 0,
    sortBy: ["created_date"],
    sortDesc: [true],
  });
  const [searchForm] = Form.useForm<SearchFormType>();

  const handleSearch = useCallback(async () => {
    if (!detailRegistration?.licenseRegistrationId) return;
    const { issuedDate, expiredDate } = searchForm.getFieldsValue();

    const formattedIssuedDate = issuedDate
      ? issuedDate.map((date: Date) => DateHelper.convertDateToStringDate(date))
      : null;
    const formattedExpiredDate = expiredDate
      ? expiredDate.map((date: Date) =>
          DateHelper.convertDateToStringDate(date),
        )
      : null;
    const request: SearchLicenseRequest = {
      licenseRegistrationId: detailRegistration.licenseRegistrationId,
      ...searchForm.getFieldsValue(),
      ...pagination,
      issuedDate: formattedIssuedDate,
      expiredDate: formattedExpiredDate,
    };
    const res: SearchLicenseResponse | null | undefined =
      await LicenseService.search(request);
    setTotalRecord(res?.total || 0);
    setDataTable(res?.items || []);
  }, [detailRegistration?.licenseRegistrationId, pagination, searchForm]);

  const columns: TableProps<LicenseItemSearch>["columns"] = LicenseTableHeader(
    t,
    detailRegistration,
  );

  const onChangePageNum = (page: number) => {
    setPagination((prevState) => ({
      ...prevState,
      page: page - 1,
    }));
  };

  const onChangePageSize = (pageSize: number) => {
    setPagination((prevState) => ({
      ...prevState,
      page: 0,
      size: pageSize,
    }));
  };

  useEffect(() => {
    handleSearch().then();
  }, [handleSearch]);

  return (
    <>
      <div className={"p-5 rounded-lg border bg-white"}>
        {/* Title */}
        <h2>{t("title.licenseManagementList")}</h2>

        {/* Table function block */}
        <div className={"mt-3"}>
          <Table
            rowKey={"licenseId"}
            columns={columns}
            dataSource={dataTable}
            pagination={false}
          />
          <AppPagination
            className={"my-3"}
            pageSize={pagination.size}
            onChangePageSize={onChangePageSize}
            onChangePage={onChangePageNum}
            currentPage={pagination.page}
            total={totalRecord}
          />
        </div>
      </div>
    </>
  );
};

export default LicenseManagement;
